<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p class="text-gray-600">Last Updated: October 23, 2025</p>
      </div>

      <!-- Content -->
      <div class="bg-white shadow-sm rounded-lg p-8 space-y-8">
        <!-- Introduction -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
          <p class="text-gray-700 leading-relaxed">
            Welcome to Peachy Pro Studios ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application and mobile application (collectively, the "Services") for video editing, processing, and content creation.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            By using our Services, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our Services.
          </p>
        </section>

        <!-- Information We Collect -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">2.1 Personal Information</h3>
          <p class="text-gray-700 leading-relaxed mb-2">We may collect the following personal information:</p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Name and contact information (email address, phone number)</li>
            <li>Account credentials (username, password)</li>
            <li>Payment and billing information</li>
            <li>Profile information and preferences</li>
            <li>Organization and team member information</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">2.2 Content and Media Files</h3>
          <p class="text-gray-700 leading-relaxed mb-2">When you use our Services, we collect and process:</p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Photos, videos, and images you upload for editing and processing</li>
            <li>Audio files and voice recordings</li>
            <li>Project files, templates, and creative assets</li>
            <li>Metadata associated with your media files (file size, format, duration, timestamps)</li>
            <li>Edited and processed versions of your content</li>
            <li>Comments, annotations, and revision notes</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">2.3 Usage Information</h3>
          <p class="text-gray-700 leading-relaxed mb-2">We automatically collect certain information about your use of the Services:</p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Device information (device type, operating system, browser type)</li>
            <li>IP address and location data</li>
            <li>Usage statistics and activity logs</li>
            <li>Application performance and error reports</li>
            <li>Feature usage and interaction patterns</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">2.4 Cookies and Tracking Technologies</h3>
          <p class="text-gray-700 leading-relaxed">
            We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities and to remember your preferences. You can control cookie settings through your browser preferences.
          </p>
        </section>

        <!-- How We Use Your Information -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
          <p class="text-gray-700 leading-relaxed mb-2">We use the collected information for the following purposes:</p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li><strong>Service Delivery:</strong> To provide, maintain, and improve our video editing and processing services</li>
            <li><strong>Content Processing:</strong> To edit, process, render, and deliver your photos and videos according to your instructions</li>
            <li><strong>Account Management:</strong> To create and manage your account, authenticate users, and provide customer support</li>
            <li><strong>Communication:</strong> To send you service updates, technical notices, security alerts, and administrative messages</li>
            <li><strong>Quality Improvement:</strong> To analyze usage patterns and improve our Services, features, and user experience</li>
            <li><strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and unauthorized access</li>
            <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our terms of service</li>
            <li><strong>Analytics:</strong> To understand how users interact with our Services and optimize performance</li>
          </ul>
        </section>

        <!-- How We Process Your Media -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">4. How We Process Your Media Content</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">4.1 Processing Activities</h3>
          <p class="text-gray-700 leading-relaxed">
            When you upload photos, videos, or other media files to our Services, we process them to provide editing, enhancement, and delivery services. This may include:
          </p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
            <li>Transcoding and format conversion</li>
            <li>Video editing, trimming, and assembly</li>
            <li>Applying filters, effects, and enhancements</li>
            <li>Audio processing and synchronization</li>
            <li>Generating thumbnails and preview versions</li>
            <li>Creating lower-resolution versions for review purposes</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Content Ownership</h3>
          <p class="text-gray-700 leading-relaxed">
            You retain all ownership rights to your original content. We do not claim any ownership over your photos, videos, or creative work. We only process your content as necessary to provide the Services you've requested.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">4.3 Automated Processing</h3>
          <p class="text-gray-700 leading-relaxed">
            Some content processing may be performed automatically using software algorithms and artificial intelligence. This helps us deliver faster results and improve service quality.
          </p>
        </section>

        <!-- Data Storage and Security -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">5. Data Storage and Security</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">5.1 Storage</h3>
          <p class="text-gray-700 leading-relaxed">
            Your data is stored on secure cloud servers provided by trusted third-party service providers. We use industry-standard encryption for data at rest and in transit.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">5.2 Security Measures</h3>
          <p class="text-gray-700 leading-relaxed mb-2">We implement appropriate technical and organizational security measures including:</p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Encryption of data in transit (SSL/TLS) and at rest</li>
            <li>Regular security assessments and monitoring</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Secure backup and disaster recovery procedures</li>
            <li>Employee training on data protection practices</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">5.3 Data Retention</h3>
          <p class="text-gray-700 leading-relaxed">
            We retain your personal information and content for as long as necessary to provide the Services and comply with legal obligations. You may request deletion of your data at any time, subject to legal retention requirements.
          </p>
        </section>

        <!-- Sharing of Information -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">6. Sharing of Information</h2>
          <p class="text-gray-700 leading-relaxed mb-2">We may share your information in the following circumstances:</p>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">6.1 Service Providers</h3>
          <p class="text-gray-700 leading-relaxed">
            We may share data with third-party service providers who perform services on our behalf, such as cloud hosting, payment processing, analytics, and customer support. These providers are contractually obligated to protect your data.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">6.2 Team Members and Organizations</h3>
          <p class="text-gray-700 leading-relaxed">
            If you are part of an organization or team, your content and project information may be shared with designated team members and administrators within your organization.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">6.3 Legal Requirements</h3>
          <p class="text-gray-700 leading-relaxed">
            We may disclose your information if required by law, court order, or governmental request, or to protect our rights, property, or safety.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">6.4 Business Transfers</h3>
          <p class="text-gray-700 leading-relaxed">
            In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">6.5 With Your Consent</h3>
          <p class="text-gray-700 leading-relaxed">
            We may share your information for any other purpose with your explicit consent.
          </p>
        </section>

        <!-- Your Rights and Choices -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights and Choices</h2>
          <p class="text-gray-700 leading-relaxed mb-2">You have the following rights regarding your personal information:</p>
          <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information and content</li>
            <li><strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
            <li><strong>Object to Processing:</strong> Object to certain processing activities</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where consent is the legal basis</li>
            <li><strong>Opt-Out:</strong> Opt-out of marketing communications</li>
          </ul>
          <p class="text-gray-700 leading-relaxed mt-4">
            To exercise these rights, please contact us using the information provided in Section 12.
          </p>
        </section>

        <!-- Children's Privacy -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h2>
          <p class="text-gray-700 leading-relaxed">
            Our Services are not intended for children under the age of 13 (or the applicable age of consent in your jurisdiction). We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately, and we will take steps to delete such information.
          </p>
        </section>

        <!-- International Data Transfers -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">9. International Data Transfers</h2>
          <p class="text-gray-700 leading-relaxed">
            Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
          </p>
        </section>

        <!-- Third-Party Links and Services -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">10. Third-Party Links and Services</h2>
          <p class="text-gray-700 leading-relaxed">
            Our Services may contain links to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any information.
          </p>
        </section>

        <!-- Changes to This Privacy Policy -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
          <p class="text-gray-700 leading-relaxed">
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date. Your continued use of the Services after changes become effective constitutes acceptance of the updated Privacy Policy.
          </p>
        </section>

        <!-- Contact Information -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
          <p class="text-gray-700 leading-relaxed mb-4">
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>
          <div class="bg-gray-50 p-6 rounded-lg">
            <p class="text-gray-700"><strong>Peachy Pro Studios</strong></p>
            <p class="text-gray-700 mt-2">Email: <a href="mailto:privacy@peachyprostudios.com" class="text-blue-600 hover:text-blue-800">privacy@peachyprostudios.com</a></p>
            <p class="text-gray-700 mt-1">Support: <a href="mailto:support@peachyprostudios.com" class="text-blue-600 hover:text-blue-800">support@peachyprostudios.com</a></p>
          </div>
        </section>

        <!-- GDPR and CCPA Specific Rights -->
        <section>
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">13. Additional Rights for Residents of Specific Jurisdictions</h2>
          
          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">13.1 European Union (GDPR)</h3>
          <p class="text-gray-700 leading-relaxed">
            If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR), including the right to lodge a complaint with a supervisory authority.
          </p>

          <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">13.2 California Residents (CCPA)</h3>
          <p class="text-gray-700 leading-relaxed">
            California residents have specific rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected, the right to delete personal information, and the right to opt-out of the sale of personal information. We do not sell your personal information.
          </p>
        </section>

        <!-- Consent -->
        <section class="border-t border-gray-200 pt-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">14. Consent</h2>
          <p class="text-gray-700 leading-relaxed">
            By using our Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with any part of this Privacy Policy, you must discontinue use of our Services immediately.
          </p>
        </section>
      </div>

      <!-- Back to Home -->
      <div class="mt-8 text-center">
        <NuxtLink to="/" class="text-blue-600 hover:text-blue-800 font-medium">
          ← Back to Home
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// Set page metadata
useHead({
  title: 'Privacy Policy - Peachy Pro Studios',
  meta: [
    {
      name: 'description',
      content: 'Privacy Policy for Peachy Pro Studios web and mobile applications. Learn how we collect, use, and protect your personal information and media content.'
    }
  ]
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style>